/**
 * ConfidenceIndicator - Visual display of response confidence level
 * 
 * Design principles:
 * - Unobtrusive but informative
 * - Color-coded for quick scanning (green=high, yellow=medium, red=low)
 * - Tooltip provides context on hover
 * - Animated fill for visual interest
 */

import React, { useMemo } from 'react';
import { Tooltip } from '../../design-system';

interface ConfidenceIndicatorProps {
  confidence: number; // 0.0 - 1.0
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const getConfidenceLevel = (confidence: number): {
  label: string;
  color: string;
  bgColor: string;
  textColor: string;
  description: string;
} => {
  if (confidence >= 0.8) {
    return {
      label: 'High confidence',
      color: 'var(--color-success-500)',
      bgColor: 'var(--color-success-50)',
      textColor: 'var(--color-success-700)',
      description: 'This answer is well-supported by available sources.',
    };
  }
  if (confidence >= 0.5) {
    return {
      label: 'Medium confidence',
      color: 'var(--color-warning-500)',
      bgColor: 'var(--color-warning-50)',
      textColor: 'var(--color-warning-700)',
      description: 'Some parts of this answer may need verification.',
    };
  }
  return {
    label: 'Low confidence',
    color: 'var(--color-error-500)',
    bgColor: 'var(--color-error-50)',
    textColor: 'var(--color-error-700)',
    description: 'This answer has limited source support. Consider asking for more details.',
  };
};

export const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  confidence,
  showLabel = false,
  size = 'sm',
  className = '',
}) => {
  const level = useMemo(() => getConfidenceLevel(confidence), [confidence]);
  const percentage = Math.round(confidence * 100);

  const sizeClasses = {
    sm: { bar: 'h-1 w-16', text: 'text-[10px]' },
    md: { bar: 'h-1.5 w-20', text: 'text-xs' },
    lg: { bar: 'h-2 w-24', text: 'text-sm' },
  };

  const indicator = (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Progress bar */}
      <div
        className={`${sizeClasses[size].bar} rounded-full overflow-hidden`}
        style={{ backgroundColor: 'var(--color-neutral-200)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${percentage}%`,
            backgroundColor: level.color,
          }}
        />
      </div>

      {/* Label */}
      {showLabel && (
        <span
          className={`${sizeClasses[size].text} font-medium`}
          style={{ color: level.textColor }}
        >
          {percentage}%
        </span>
      )}
    </div>
  );

  return (
    <Tooltip content={`${level.label}: ${level.description}`}>
      {indicator}
    </Tooltip>
  );
};

/**
 * ConfidenceBadge - Compact badge variant for inline use
 */
export const ConfidenceBadge: React.FC<{
  confidence: number;
  className?: string;
}> = ({ confidence, className = '' }) => {
  const level = useMemo(() => getConfidenceLevel(confidence), [confidence]);
  const percentage = Math.round(confidence * 100);

  return (
    <Tooltip content={level.description}>
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${className}`}
        style={{
          backgroundColor: level.bgColor,
          color: level.textColor,
        }}
      >
        <svg
          className="w-2.5 h-2.5"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="8"
            cy="8"
            r="6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${(confidence * 37.7)} 37.7`}
            transform="rotate(-90 8 8)"
            opacity="0.3"
          />
          <circle
            cx="8"
            cy="8"
            r="6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${(confidence * 37.7)} 37.7`}
            transform="rotate(-90 8 8)"
          />
        </svg>
        {percentage}%
      </span>
    </Tooltip>
  );
};

export default ConfidenceIndicator;

